import { EFinancingProgram, EPaymentFrequency, EProvince, PlanPromoRule } from '@if/core/types'
import { ETermMax } from '@src/data/types/constants'
import { NormCreditDecision } from '@src/data/types/CreditDecisionSchema'
import { MerchantPaymentPlan } from '@src/data/types/MerchantPaymentPlan'
import { CustomPromoPlanOption } from '@src/data/types/PromoPlan'
import { createContext, useContext } from 'react'

export interface BaseWorksheetFormConfig {
  financingProgramId: EFinancingProgram
  creditApplicationId: string
  merchantId: string

  minLoanAmount: number
  maxLoanAmount: number

  minLoanTerm: number
  maxLoanTerm: number
  largeLoanAmount: number
  longTerm: number

  effectiveRateThreshold: number

  supportedTerms: ETermMax[]
  supportedPaymentFrequencies: EPaymentFrequency[]
  supportsInsurance: boolean
  supportsHighEstimate: boolean
  useAgentLeeway: boolean
  validateMaxPayment: boolean
  filteredTermsByMaxPayment: boolean
  checkAvailableCredit: boolean
  checkActivationWithin90Days: boolean

  normsCreditDecision: NormCreditDecision | null
  maxAmountFinanced?: number | null
  maxTermDuration?: number | null
  canAcceptHighEstimate?: boolean

  hasCoApplicant: boolean
  stateIso: EProvince
  interestRate: number
  maxPmtAmount?: number

  plans: MerchantPaymentPlan[]
  availablePaymentPlans: MerchantPaymentPlan[]
  planPromoRules?: PlanPromoRule[]
  // On the Custom PromoPlan system (renders the toggle instead of the legacy plan select); not that a promo is active.
  isCustomPromoPlanMerchant: boolean
  isPaymentPlanLoading: boolean
  // Currently active Custom PromoPlan, or null when none is active (none configured, future, or expired).
  customPromoPlan: CustomPromoPlanOption | null
  createdOn: Date

  expiresOn: Date | string
  editLocked?: boolean
  isSinglePayment?: boolean
}

const BaseWorksheetFormConfigContext = createContext<BaseWorksheetFormConfig | null>(null)

export const BaseWorksheetFormConfigProvider = BaseWorksheetFormConfigContext.Provider

export const useBaseWorksheetFormConfig = (): BaseWorksheetFormConfig => {
  const value = useContext(BaseWorksheetFormConfigContext)
  if (!value) {
    throw new Error('useBaseWorksheetFormConfig must be used inside <BaseWorksheetForm config={...}>')
  }
  return value
}

const REQUIRED_FINANCING_CONFIG_KEYS = [
  'effectiveRateThreshold',
  'minLoanAmount',
  'maxLoanAmount',
  'minLoanTerm',
  'maxLoanTerm',
] as const satisfies readonly (keyof BaseWorksheetFormConfig)[]

export type RequiredFinancingConfigKey = (typeof REQUIRED_FINANCING_CONFIG_KEYS)[number]

export const findMissingFinancingConfigKeys = (
  config: Pick<BaseWorksheetFormConfig, RequiredFinancingConfigKey>,
): RequiredFinancingConfigKey[] => REQUIRED_FINANCING_CONFIG_KEYS.filter((key) => !config[key])
